import gql from 'graphql-tag';
import { compose, graphql } from 'react-apollo';
import { fromIso9075DateString } from 'utils/dates';
import fragments from 'utils/graphql/fragments';
import withMountCallback from 'utils/withMountCallback';

const RANKING_FIELDS = gql`
  fragment RankingFields on QuestLeaderboardRanking {
    user {
      ...UserIdAndDisplayFields
    }
    score
    displayScore
    isComplete
    rank
  }
  ${fragments.userIdAndDisplay}
`;

const QUEST_LEADERBOARD_QUERY = gql`
  query MyGetQuestLeaderboard($input: GetQuestLeaderboard!) {
    questLeaderboard(input: $input) {
      questId
      totalPlayers
      winningPlaceCount
      lastUpdated
      rankings {
        ...RankingFields
      }
      myRanking {
        ...RankingFields
      }
    }
  }
  ${RANKING_FIELDS}
`;

const QUEST_LEADERBOARD_SUBSCRIPTION = gql`
  subscription MyQuestLeaderboardUpdates(
    $input: QuestLeaderboardUpdatesInput!
  ) {
    questLeaderboardUpdates(input: $input) {
      questId
      totalPlayers
      lastUpdated
      rankings {
        ...RankingFields
      }
      myRanking {
        ...RankingFields
      }
      rankingShiftStart
      rankingShiftEnd
    }
  }
  ${RANKING_FIELDS}
`;

const withData = graphql(QUEST_LEADERBOARD_QUERY, {
  name: 'leaderboardData',
  options: props => ({
    variables: {
      input: {
        questId: props.quest.id
      }
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps, leaderboardData }) => ({
    leaderboardData,
    subscribeToLeaderboardUpdates: () => {
      return leaderboardData.subscribeToMore({
        document: QUEST_LEADERBOARD_SUBSCRIPTION,
        variables: { input: { questId: ownProps.quest.id } },
        updateQuery: (prev, { subscriptionData: { data: newData } }) => {
          if (
            !prev ||
            !prev.questLeaderboard ||
            !newData ||
            newData.questId !== ownProps.quest.id
          ) {
            return null;
          }

          const prevData = prev.questLeaderboard;
          const updatedFields = {};
          if (newData.totalPlayers > prevData.totalPlayers) {
            updatedFields.totalPlayers = newData.totalPlayers;
          }
          if (
            fromIso9075DateString(newData.lastUpdated) >
            fromIso9075DateString(prevData.lastUpdated)
          ) {
            updatedFields.lastUpdated = newData.lastUpdated;
          }
          if (newData.rankings && newData.rankings.length > 0) {
            updatedFields.rankings = newData.rankings;
          }
          if (newData.myRanking) {
            updatedFields.myRanking = newData.myRanking;
          } else if (
            newData.rankingShiftStart &&
            prevData.myRanking &&
            prevData.myRanking.rank >= newData.rankingShiftStart &&
            (!newData.rankingShiftEnd ||
              prevData.myRanking.rank <= newData.rankingShiftEnd)
          ) {
            updatedFields.myRanking = Object.assign({}, prevData.myRanking, {
              rank: prevData.myRanking.rank + 1
            });
          }

          return Object.assign({}, prev, {
            questLeaderboard: Object.assign({}, prevData, updatedFields)
          });
        }
      });
    }
  })
});

const withMount = withMountCallback(
  props => ({ unsubscribe: props.subscribeToLeaderboardUpdates() }),
  (props, { unsubscribe }) => unsubscribe && unsubscribe()
);

export default compose(withData, withMount);
